import React, { useEffect } from 'react';
import { Button, Row, Col, Alert, Tooltip } from 'antd';
import { EyeOutlined, HistoryOutlined } from '@ant-design/icons';
import { CustomTable, GeneralContainer } from '../../../../components';
import { ShareResponse } from '../../../../share';
import { TechnicalRequestResponse } from '../../../TechnicalConcept/types/technical-concept';
import SearchRow from '../components/SearchFilters';
import { TechnicalConceptAPI } from '../../../TechnicalConcept/api/technicalConceptAPI';
import CustomAlert from '../../../../components/CustomAlert/CustomAlert';

interface ApprovalsTechnicalConceptViewProps {
  data?: TechnicalRequestResponse;
  onPagination: (page: number, pageSize: number) => void;
  onViewDetail: (id: string) => void;
  formik: any;
  isLoading: boolean;
  onResetFilters: () => void;
  sectionals?: ShareResponse[];
}

const ApprovalsTechnicalConceptView: React.FC<ApprovalsTechnicalConceptViewProps> = ({
  data,
  onViewDetail,
  onPagination,
  formik,
  isLoading,
  onResetFilters,
  sectionals,
}) => {

  const [status, setStatus] = React.useState<{ id: string; name: string }[]>([]);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const data = await TechnicalConceptAPI.fetchParameters(['concept_petition_status']);
        setStatus(data?.concept_petition_status?.map((item: any) => ({
          id: String(item.id),
          name: item.status,
        })) || []);
      } catch (error) {
        throw error;
      }
    };
    fetchStatus();
  }, [data]);


  const columns = [
    {
      title: 'Número ticket',
      dataIndex: 'ticket',
      key: 'ticket',
      sorter: true,
      align: 'center',
    },
    {
      title: 'Fecha solicitud',
      dataIndex: 'query_date',
      key: 'query_date',
      sorter: true,
      align: 'center',
    },
      {
      title: 'Estado de solicitud',
      dataIndex: 'status',
      key: 'status',
      align: 'center',
      render: (status: string) => {
        return <CustomAlert status={status} />;
      },
    },
    {
      title: 'Solicitante',
      dataIndex: 'requester',
      key: 'requester',
      align: 'center',
      responsive: ['lg'],
    },
    {
      title: 'Tipo ticket',
      dataIndex: 'ticket_type',
      key: 'ticket_type',
      sorter: true,
      align: 'center',
      responsive: ['lg'],
    },
    {
      title: 'Regional',
      dataIndex: 'regional',
      key: 'regional',
      sorter: true,
      align: 'center',
      responsive: ['lg'],
    },
    {
      title: 'Seccional',
      dataIndex: 'sectional',
      key: 'sectional',
      sorter: true,
      align: 'center',
      responsive: ['lg'],
    },
    {
      title: 'Código/placa',
      dataIndex: 'plate_code',
      key: 'plate_code',
      sorter: true,
      align: 'center',
      responsive: ['lg'],
      render: (_: any, record: any) => {
        return record.items[0]?.plate_code || 'Sin registro';
      }
    },
    {
      title: 'Clase de activo',
      dataIndex: 'asset_class',
      key: 'asset_class',
      sorter: true,
      align: 'center',
      responsive: ['lg'],
      render: (_: any, record: any) => {
        return record.items[0]?.asset_class || 'Sin registro';
      }
    },
    {
      title: 'Plazo de entrega',
      dataIndex: 'delivery_deadline',
      key: 'delivery_deadline',
      sorter: true,
      align: 'center',
      responsive: ['lg'],
      render: (_: any, record: any) => {
        return record.items[0]?.delivery_deadline || 'Sin registro';
      }
    },
    {
      title: 'Observaciones',
      dataIndex: 'observations',
      key: 'observations',
      align: 'center',
      width: 300,
      render: (text: string) => {
        return (
          <div
            style={{
              maxWidth: 250,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              margin: '0 auto',
            }}
          >
            {text || 'Sin registro'}
          </div>
        );
      },
    },
    {
      title: 'Acciones',
      key: 'actions',
      align: 'center',
      render: (_: any, record: { id: number, status_id: string }) => (
        ['67', '68'].includes(record?.status_id) ? (
        <Tooltip title="Gestionar solicitud">
          <Button
            type="link"
            icon={<HistoryOutlined />}
            onClick={() => onViewDetail(String(record.id))}
          />
        </Tooltip>
      ) : (
        <Tooltip title="Ver solicitud">
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => onViewDetail(String(record.id))}
          />
        </Tooltip>
      )
      ),
    },  
  ];

  return (
    <GeneralContainer
      title="Aprobaciones de concepto técnico"
      isLoading={isLoading}
    >
      {/* Filtros */}
      <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
        <Col xs={24}>
          <SearchRow
            sectionals={sectionals}
            formik={formik}
            states={status}
            onCancel={onResetFilters}
            isLoading={isLoading}
          />
        </Col>
      </Row>

      <Row>
        <Col xs={24}>
          <CustomTable
            loading={isLoading}
            columns={columns as any}
            dataSource={data?.data}
            paginationConfig={{
              current: data?.current_page,
              pageSize: data?.per_page,
              total: data?.total,
              onChange: onPagination,
            }}
            scroll={{ x: 'max-content' }}
          />
        </Col>
      </Row>
    </GeneralContainer>
  );
};

export default ApprovalsTechnicalConceptView;
